import React, { FC } from 'react';
import { useFormik } from 'formik';
import Yup from '~yup';

import { TCreateNewRoleReduxParams } from '~types/feat/admin';

import LabelledInput from '~components/atoms/LabelledInput';
import ActionButton from '~components/atoms/ActionButton';
import Text from '~components/atoms/Text';
import RolePermissions from '../RolesRolePermissions';

import { ModuleEnum } from '~utils/enums';
import { ModulePermissions } from '~utils/constants';
import UtilFunctions from '~utils/functions';

import * as FormStyles from './_rolesCreateRoleForm.module.scss';

const RoleCreateRoleFormValidation = Yup.object().shape({
    roleName: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().matchNumbersAndAlphabets().required('Role Name is required'),
});

type TRoleCreateRoleForm = {
    submitButtonLabel: string;
    onSubmit: (data: TCreateNewRoleReduxParams) => void;
    error: string;
    actionButtonIsLoading?: boolean;
    actionButtonClassName?: string;
    setError?: (errorString: string) => void;
}

const RoleCreateRoleForm: FC<TRoleCreateRoleForm> = ({
    submitButtonLabel,
    onSubmit,
    error,
    actionButtonIsLoading,
    actionButtonClassName,
    setError,
}) => {
    const { Misc: { blockEnterKey } } = UtilFunctions;

    const RoleCreateRoleFormInitialValues: TCreateNewRoleReduxParams = {
        roleName: '',
        access: [],
        allBranchAccess: false,
    };

    const formik = useFormik({
        initialValues: RoleCreateRoleFormInitialValues,
        validationSchema: RoleCreateRoleFormValidation,
        onSubmit,
    });

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        if (!formik.values.roleName.length && setError) {
            setError('Role Name is required!');
        }

        formik.handleSubmit(e);
    };

    // Toggling "All Branches Access" gates Market Status:
    // - turning it ON: pre-tick Market Status's permissions into access, then the user may freely
    //   toggle them further (RolesRolePermissions unlocks the checkboxes once allBranchAccess is true)
    // - turning it OFF: strip Market Status's permissions back out of access and re-lock the checkboxes
    const handleAllBranchAccessChange = () => {
        const newValue = !formik.values.allBranchAccess;

        formik.setFieldValue('allBranchAccess', newValue);

        const marketStatusPermissions = ModulePermissions[ModuleEnum.MarketStatus] || [];

        if (newValue) {
            const newAccess = marketStatusPermissions.reduce((acc, permission) => {
                const existingAccessIndex = acc.findIndex(access => access.permission === permission);

                if (existingAccessIndex >= 0) {
                    const existingModules = acc[existingAccessIndex].modules;

                    if (existingModules.includes(ModuleEnum.MarketStatus)) {
                        return acc;
                    }

                    const updatedEntry = {
                        ...acc[existingAccessIndex],
                        modules: [...existingModules, ModuleEnum.MarketStatus],
                    };

                    return [
                        ...acc.slice(0, existingAccessIndex),
                        updatedEntry,
                        ...acc.slice(existingAccessIndex + 1),
                    ];
                }

                return [
                    ...acc,
                    { permission, modules: [ModuleEnum.MarketStatus] },
                ];
            }, formik.values.access);

            formik.setFieldValue('access', newAccess);
        } else {
            const newAccess = formik.values.access.map(access => ({
                ...access,
                modules: access.modules.filter(module => module !== ModuleEnum.MarketStatus),
            }));

            formik.setFieldValue('access', newAccess);
        }
    };

    return (
        <form
            onSubmit={handleSubmit}
            className={FormStyles.container}
            onKeyDown={blockEnterKey}
        >
            <div className={FormStyles['form-container']}>
                <div className={FormStyles['form-subcontainer']}>
                    <LabelledInput
                        id='roleName'
                        name='roleName'
                        label='Role Name* :'
                        value={formik.values.roleName}
                        error={formik.touched.roleName ? formik.errors.roleName : undefined}
                        onChange={(e) => {
                            if (error && setError) setError('');

                            formik.handleChange(e);
                        }}
                        onBlur={formik.handleBlur}
                        className={FormStyles['role-name-input']}
                    />

                    <LabelledInput
                        label='All Branches Access: '
                        onChange={handleAllBranchAccessChange}
                        type='checkbox'
                        checked={formik.values.allBranchAccess}
                        className={FormStyles.checkbox}
                    />
                </div>

                <RolePermissions
                    accessList={formik.values.access}
                    handleChange={formik.handleChange}
                    allBranchAccess={formik.values.allBranchAccess}
                />

                <div className={`${FormStyles['button-container']} ${FormStyles['span-2']}`}>
                    <div className={FormStyles['button-subcontainer']}>
                        <ActionButton
                            label={submitButtonLabel}
                            isLoading={actionButtonIsLoading}
                            type='submit'
                            disabled={actionButtonIsLoading}
                            className={actionButtonClassName}
                        />

                        <Text
                            label={error}
                            isError
                        />
                    </div>
                </div>
            </div>
        </form>
    );
};

export default RoleCreateRoleForm;
